import { IconChevronBottom } from 'central-icons/IconChevronBottom';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import ReactDatePicker from 'react-datepicker';
import type { MultiValue, SingleValue } from 'react-select';
import { useCookie } from 'react-use';

import type { UserOverrides } from '@revelio/auth';
import { InterestedOptions, TourCustomPageValue } from '@revelio/core';
import type { Role, TrialType } from '@revelio/data-access';
import { Button, FormField, Input, Select, Switch } from '@revelio/ui';

import { FieldGroup } from '../../../../shared/ui/admin/groups/field/field-group';
import { videoMetadata } from '../../../../shared/ui/tutorial/Tutorial';
import fieldStyles from '../../user-form.module.css';
import {
  DEFAULT_TRIAL_EXPIRATION_DAYS,
  getTrialExpirationDate,
  type SelectOption,
} from './impersonationModal.utils';
import styles from './impersonationOverrides.module.css';

type TourInterestOptionValue =
  | 'talentDiscovery'
  | 'dataBuilder'
  | 'talentDiscoveryAndDataBuilder';

const TOUR_INTEREST_OPTIONS: SelectOption<TourInterestOptionValue>[] = [
  { label: 'Talent Discovery', value: 'talentDiscovery' },
  { label: 'Data Builder', value: 'dataBuilder' },
  {
    label: 'Talent Discovery + Data Builder',
    value: 'talentDiscoveryAndDataBuilder',
  },
];

const parseTourInterestsFromCookie = (
  cookieValue?: string | null
): string[] => {
  if (!cookieValue) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(cookieValue) as unknown;
    if (Array.isArray(parsedValue)) {
      return parsedValue.filter(
        (value): value is string => typeof value === 'string'
      );
    }
  } catch {
    // Cookie can contain a single non-JSON interest value; fall through.
  }

  return cookieValue
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
};

const parseStepsCompleted = (value?: string): string[] => {
  if (!value) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value) as unknown;
    if (Array.isArray(parsedValue)) {
      return parsedValue.filter(
        (item): item is string => typeof item === 'string'
      );
    }
  } catch {
    return [];
  }

  return [];
};

interface ImpersonationUserOverridesFormProps {
  customOverrides: UserOverrides;
  setCustomOverrides: Dispatch<SetStateAction<UserOverrides>>;
  onReset: () => void;
  trialTypeOptions: SelectOption<TrialType>[];
  selectedTrialTypeOption: SelectOption<TrialType> | undefined;
  roleOptions: SelectOption<Role>[];
  selectedRoleOption: SelectOption<Role> | undefined;
}

export function ImpersonationUserOverridesForm({
  customOverrides,
  setCustomOverrides,
  onReset,
  trialTypeOptions,
  selectedTrialTypeOption,
  roleOptions,
  selectedRoleOption,
}: ImpersonationUserOverridesFormProps) {
  const [tourCustomPageValue] = useCookie(TourCustomPageValue);
  const cookieTourInterests = useMemo(
    () => parseTourInterestsFromCookie(tourCustomPageValue),
    [tourCustomPageValue]
  );
  const hasTalentAcquisitionTourInterest =
    customOverrides.tourInterestTalentAcquisition ??
    cookieTourInterests.includes(InterestedOptions.Talent_Acquisition);
  const hasCustomDataTourInterest =
    customOverrides.tourInterestCustomData ??
    cookieTourInterests.includes(
      InterestedOptions.Data_Enrichment_and_Custom_Reports
    );
  const selectedTourInterestOption = useMemo(() => {
    if (hasTalentAcquisitionTourInterest && hasCustomDataTourInterest) {
      return TOUR_INTEREST_OPTIONS.find(
        (option) => option.value === 'talentDiscoveryAndDataBuilder'
      );
    }

    if (hasTalentAcquisitionTourInterest) {
      return TOUR_INTEREST_OPTIONS.find(
        (option) => option.value === 'talentDiscovery'
      );
    }

    if (hasCustomDataTourInterest) {
      return TOUR_INTEREST_OPTIONS.find(
        (option) => option.value === 'dataBuilder'
      );
    }

    return undefined;
  }, [hasTalentAcquisitionTourInterest, hasCustomDataTourInterest]);
  const tutorialStepOptions = useMemo<SelectOption<string>[]>(
    () =>
      videoMetadata.flatMap(({ videos }) =>
        videos.map((video) => ({
          label: `${video.name} (${video.id})`,
          value: video.id,
        }))
      ),
    []
  );
  const selectedTutorialStepOptions = useMemo(() => {
    const seenSteps = new Set(
      parseStepsCompleted(customOverrides.stepsCompleted)
    );
    return tutorialStepOptions.filter((option) => seenSteps.has(option.value));
  }, [customOverrides.stepsCompleted, tutorialStepOptions]);

  return (
    <FieldGroup
      title="User Settings"
      description="Overrides applied to the impersonated user."
      action={
        <Button
          size="xs"
          variant="ghost"
          color="destructive"
          onClick={onReset}
          data-testid="reset-user-overrides-button"
        >
          Reset User Overrides
        </Button>
      }
    >
      <div className={`${fieldStyles.fields} ${styles.column}`}>
        <FormField
          label="Trial User"
          className={`${fieldStyles.row} ${fieldStyles.endRow} ${fieldStyles.bareSwitch} ${styles.switchRow}`}
        >
          <Switch
            size="sm"
            inactiveLabel="Off"
            label="On"
            checked={customOverrides.isTrialUser ?? false}
            onChange={(event) =>
              setCustomOverrides((prev) => ({
                ...prev,
                isTrialUser: event.target.checked,
                live: !event.target.checked,
                expirationDate: event.target.checked
                  ? prev.expirationDate ||
                    getTrialExpirationDate(DEFAULT_TRIAL_EXPIRATION_DAYS)
                  : undefined,
              }))
            }
            data-testid="trial-user-toggle"
          />
        </FormField>

        <FormField
          label="Trial Type"
          htmlFor="impersonation-trial-type"
          className={styles.stackedRow}
        >
          <Select<SelectOption<TrialType>, false>
            inputId="impersonation-trial-type"
            options={trialTypeOptions}
            value={selectedTrialTypeOption ?? null}
            placeholder="Select trial type..."
            onChange={(selected: SingleValue<SelectOption<TrialType>>) =>
              setCustomOverrides((prev) => ({
                ...prev,
                trialType: selected?.value,
              }))
            }
            isClearable={true}
            data-testid="trial-type-select"
          />
        </FormField>

        <FormField label="Trial End Date" className={styles.stackedRow}>
          <ReactDatePicker
            selected={
              customOverrides.expirationDate
                ? new Date(customOverrides.expirationDate)
                : null
            }
            onChange={(newDate: Date | null) =>
              setCustomOverrides((prev) => ({
                ...prev,
                expirationDate: newDate ? newDate.toISOString() : undefined,
              }))
            }
            dateFormat="MMMM d, yyyy h:mm aa"
            showTimeSelect={true}
            yearDropdownItemNumber={15}
            scrollableYearDropdown
            showYearDropdown={false}
            dropdownMode="select"
            customInput={<Input size="sm" />}
          />
        </FormField>

        <FormField
          label="Role Override"
          htmlFor="impersonation-role-override"
          className={styles.stackedRow}
        >
          <Select<SelectOption<Role>, false>
            inputId="impersonation-role-override"
            options={roleOptions}
            value={selectedRoleOption ?? null}
            placeholder="Select role..."
            onChange={(selected: SingleValue<SelectOption<Role>>) =>
              setCustomOverrides((prev) => ({
                ...prev,
                role: selected?.value,
              }))
            }
            isClearable={true}
            data-testid="role-override-select"
          />
        </FormField>

        <details className={styles.tourAccordionItem}>
          <summary
            className={styles.tourAccordionButton}
            data-testid="user-tour-toggle"
          >
            <span className={styles.subHeading}>User Tour</span>
            <IconChevronBottom
              size={16}
              ariaHidden={true}
              className={styles.tourChevron}
            />
          </summary>
          <div className={styles.tourAccordionPanel}>
            <div className={`${fieldStyles.fields} ${styles.column}`}>
              <FormField
                label="Tour Completed"
                className={`${fieldStyles.row} ${fieldStyles.endRow} ${fieldStyles.bareSwitch} ${styles.switchRow}`}
              >
                <Switch
                  size="sm"
                  inactiveLabel="Off"
                  label="On"
                  checked={customOverrides.tourCompleted ?? false}
                  onChange={(event) =>
                    setCustomOverrides((prev) => ({
                      ...prev,
                      tourCompleted: event.target.checked,
                    }))
                  }
                  data-testid="tour-completed-toggle"
                />
              </FormField>

              <FormField
                label="Interests Included in Tour"
                htmlFor="impersonation-tour-interests"
                className={styles.stackedRow}
              >
                <Select<SelectOption<TourInterestOptionValue>, false>
                  inputId="impersonation-tour-interests"
                  options={TOUR_INTEREST_OPTIONS}
                  value={selectedTourInterestOption ?? null}
                  placeholder="Use current tour interests"
                  onChange={(
                    selected: SingleValue<SelectOption<TourInterestOptionValue>>
                  ) =>
                    setCustomOverrides((prev) => {
                      if (!selected) {
                        return {
                          ...prev,
                          tourInterestTalentAcquisition: undefined,
                          tourInterestCustomData: undefined,
                        };
                      }

                      if (selected.value === 'talentDiscovery') {
                        return {
                          ...prev,
                          tourInterestTalentAcquisition: true,
                          tourInterestCustomData: false,
                        };
                      }

                      if (selected.value === 'dataBuilder') {
                        return {
                          ...prev,
                          tourInterestTalentAcquisition: false,
                          tourInterestCustomData: true,
                        };
                      }

                      return {
                        ...prev,
                        tourInterestTalentAcquisition: true,
                        tourInterestCustomData: true,
                      };
                    })
                  }
                  isClearable={true}
                  data-testid="tour-interest-select"
                />
              </FormField>

              <FormField
                label="Tutorial Steps Seen"
                htmlFor="impersonation-tutorial-steps"
                className={styles.stackedRow}
                supportingText="This edits the metadata value `steps_completed` directly."
              >
                <Select<SelectOption<string>, true>
                  inputId="impersonation-tutorial-steps"
                  isMulti
                  options={tutorialStepOptions}
                  value={selectedTutorialStepOptions}
                  placeholder="Choose seen tutorial steps..."
                  onChange={(selected: MultiValue<SelectOption<string>>) =>
                    setCustomOverrides((prev) => ({
                      ...prev,
                      stepsCompleted: JSON.stringify(
                        selected.map((option) => option.value)
                      ),
                    }))
                  }
                  isClearable={true}
                  closeMenuOnSelect={false}
                  data-testid="tutorial-steps-seen-select"
                />
              </FormField>
            </div>
          </div>
        </details>
      </div>
    </FieldGroup>
  );
}
